import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatAmount } from '@shared/lib/format';
import type { Receipt } from '@features/receipts';

interface MonthPoint {
  month: string;
  total: number;
}

const MONTH_LABELS = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];

function aggregateByMonth(receipts: Receipt[]): MonthPoint[] {
  const now = new Date();
  const points: MonthPoint[] = [];

  for (let offset = 4; offset >= 0; offset--) {
    const ref = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, '0')}`;
    const total = receipts
      .filter((r) => r.date.startsWith(key))
      .reduce((sum, r) => sum + r.total, 0);
    points.push({ month: MONTH_LABELS[ref.getMonth()], total: Math.round(total * 100) / 100 });
  }
  return points;
}

/** Formatte un montant en axe compact (1 200 → 1,2 k€). */
function formatCompact(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)} k€`;
  return `${value} €`;
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-lg shadow-slate-900/5">
      <p className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">{label}</p>
      <div className="mt-0.5 flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-emerald-500" />
        <p className="text-sm font-semibold text-slate-900">
          {formatAmount(payload[0].value, 'EUR')}
        </p>
      </div>
    </div>
  );
}

/**
 * « Expenses Overview » — area chart des dépenses mensuelles.
 * Ligne de moyenne en pointillés, dégradé émeraude, dernier point mis en avant.
 */
export function ExpensesAreaChart({ receipts }: { receipts: Receipt[] }) {
  const data = aggregateByMonth(receipts);
  const hasData = data.some((point) => point.total > 0);

  if (!hasData) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-slate-400">
        Aucune dépense sur la période.
      </div>
    );
  }

  const average = data.reduce((sum, p) => sum + p.total, 0) / data.length;

  return (
    <div className="h-64 w-full" role="img" aria-label="Dépenses mensuelles">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 12, right: 8, bottom: 0, left: -16 }}>
          <defs>
            <linearGradient id="expensesFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#059669" stopOpacity={0.22} />
              <stop offset="60%" stopColor="#059669" stopOpacity={0.05} />
              <stop offset="100%" stopColor="#059669" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: '#94a3b8' }}
            axisLine={false}
            tickLine={false}
            dy={6}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#94a3b8' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatCompact}
            width={56}
          />
          <ReferenceLine
            y={average}
            stroke="#cbd5e1"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={{
              value: `Moy. ${formatCompact(Math.round(average))}`,
              position: 'insideTopRight',
              fontSize: 11,
              fill: '#94a3b8',
            }}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }} />
          <Area
            type="monotone"
            dataKey="total"
            stroke="#059669"
            strokeWidth={2.5}
            strokeLinecap="round"
            fill="url(#expensesFill)"
            dot={{ r: 3, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
